import * as React from 'react'
import { Button } from '~/sotano-kit/primitives/button'
import { AgentBlock, Block } from '@basehub/data/replicache/index'
import { clsx } from 'clsx'
import { isHotkey } from 'is-hotkey'
import ReactTextareaAutosize from 'react-textarea-autosize'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { useTabsStore } from '../../tabs/store'
import { useEditorMode } from '../../editor-mode-provider'
import { useEditorStore } from '../../editor-store'
import { PrimitiveTipTapEditor } from '~/lib/tiptap/components/primitive-editor'
import { Document } from '@tiptap/extension-document'
import { Text } from '@tiptap/extension-text'
import { Paragraph } from '@tiptap/extension-paragraph'
import { HardBreak } from '@tiptap/extension-hard-break'
import { Extensions } from '@tiptap/core'
import { Placeholder } from '@tiptap/extension-placeholder'
import {
  CollaborationExtensionsOptions,
  getCollaborationExtensions,
} from '~/lib/tiptap/extensions/collaboration'
import {
  useBlockAwareness,
  useCollaborationData,
} from '~/hooks-data/use-repo-presence'
import { SYSTEM_AGENTS } from '@basehub/data/replicache/schema/block/agents'
import { toast } from 'sonner'
import { useUploadFileToRepoMutation } from '~/hooks-data/use-upload-file-to-repo-mutation'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { STARTIcon } from '~/components/icons/START'
import './agent-editor.scss'
import { isValidImageType } from '@basehub/data/replicache/schema/block/valid-mime-types'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  RepoUndoOps,
  RepoUndoStackItem,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'

const getSimpleExtensions = (options: CollaborationExtensionsOptions) => {
  return [
    Placeholder.configure({
      placeholder: 'Enter your system prompt here...',
    }),
    Document.configure(),
    Text.configure(),
    Paragraph.configure(),
    HardBreak.configure(),
    ...getCollaborationExtensions(options),
  ] satisfies Extensions
}

export const AgentInput = ({
  block,
  path,
}: {
  block: AgentBlock
  parent?: Block
  path: string
}) => {
  const { replicache, team, repoId } = useRepoReplicache().data ?? {}
  const teamId = team?.id
  const sourceBlockId = idToSourceBlockId(block)
  const undoManager = useRepoUndoManager()
  const blockRef = React.useRef(block)
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const editorMode = useEditorMode()
  const setAiGeneration = useEditorStore((s) => s.setAiGeneration)

  const setSelectedBlock = useEditorStore(
    (s) => () => s.setSelectedBlockId(idToSourceBlockId(block))
  )

  React.useEffect(() => {
    setSelectedBlock()
  }, [setSelectedBlock])

  const {
    ref: titleRef,
    onChange: onTitleChange,
    value: titleSyncedValue,
  } = useSyncedValue<
    HTMLTextAreaElement,
    { value: string; parentStackItem: RepoUndoStackItem | null }
  >({
    value: React.useMemo(
      () => ({ value: block.title, parentStackItem: null }),
      [block.title]
    ),
    customEventsKey: `sync-document-title:${sourceBlockId}`,
    setValue: async ({ value, parentStackItem }, { internalValueRef }) => {
      const block = blockRef.current
      if (!block || !replicache || !path) return
      const getParams = (v: string): UpdateBlockParams => {
        return {
          id: block.id,
          data: {
            title: v,
            type: block.type,
          },
          instanceChildTarget: block.instanceChildTarget ?? null,
          strategy: 'merge',
          path,
          isoNow: new Date().toISOString(),
        }
      }
      const result = await replicache.mutate.updateBlock(getParams(value))
      if (result.undo) {
        const stackItem: RepoUndoStackItem = {
          redo: [
            getBlockFocusOp({
              block: {
                id: block.id,
                sourceBlockId: block.sourceBlockId,
              },
              focusType: 'block:document-title',
              preferredTabKey: activeTabKey,
            }),
            {
              op: 'updateBlock',
              args: getParams(internalValueRef.current.value),
            },
            {
              op: 'customWindowEvent',
              args: {
                name: `sync-document-title:${sourceBlockId}`,
                detail: { action: 'sync' },
              },
            },
          ],
          undo: [
            getBlockFocusOp({
              block: {
                id: block.id,
                sourceBlockId: block.sourceBlockId,
              },
              focusType: 'block:document-title',
              preferredTabKey: activeTabKey,
            }),
            { op: 'blockBatchMutations', args: { mutations: result.undo } },
            {
              op: 'customWindowEvent',
              args: {
                name: `sync-document-title:${sourceBlockId}`,
                detail: { action: 'sync' },
              },
            },
          ],
        }

        if (parentStackItem) {
          parentStackItem.redo.push(...stackItem.undo)
          parentStackItem.undo.push(...stackItem.undo)
        } else {
          undoManager.current?.push(stackItem)
        }
      }
    },
  })

  React.useEffect(() => {
    if (editorMode === 'write:all' || editorMode === 'write:value-and-title') {
      const timeout = setTimeout(() => {
        if (!titleRef.current) return
        titleRef.current.focus()
        titleRef.current.setSelectionRange(
          titleRef.current.value.length,
          titleRef.current.value.length
        )
      })
      return () => clearTimeout(timeout)
    }
  }, [editorMode, titleRef])

  const inputRef = React.useRef<HTMLInputElement>(null)

  const { mutateAsync } = useUploadFileToRepoMutation()

  const handleAvatarChange = React.useCallback(
    async (e: { target: { files: FileList | File[] | null } }) => {
      if (!teamId || !repoId) return
      const file = e.target.files?.[0]
      const toastId = `${block.id}-avatar`
      if (!file || !replicache) return

      if (!isValidImageType(file.type)) {
        inputRef.current?.value && (inputRef.current.value = '')
        return toast.error(`Invalid file type: ${file.type}`)
      }

      const timeout = setTimeout(() => {
        toast.error(
          `Error uploading image. This may happen if the file is an SVG which is malformed. Contact us and we'll help you out.`
        )
      }, 3000)

      const image = new Image()
      image.onload = async () => {
        clearTimeout(timeout)
        toast.loading('Uploading image...', { id: toastId })

        try {
          const { uploadURL } = await mutateAsync({
            repoId,
            teamId,
            file,
          })

          const params: UpdateBlockParams = {
            id: block.id,
            instanceChildTarget: block.instanceChildTarget,
            data: {
              type: 'agent',
              typeOptions: {
                avatar: uploadURL,
              },
            },
            path,
            strategy: 'merge-deep',
            isoNow: new Date().toISOString(),
          }
          const result = await replicache.mutate.updateBlock(params)
          if (result.undo) {
            undoManager.current?.push({
              redo: [
                {
                  op: 'focus',
                  args: {
                    preferredTabKey: activeTabKey,
                    focus: {
                      type: 'block:value',
                      blockId: idToSourceBlockId({
                        id: block.id,
                        sourceBlockId: block.sourceBlockId,
                      }),
                    },
                  },
                },
                { op: 'updateBlock', args: params },
              ],
              undo: [
                {
                  op: 'focus',
                  args: {
                    preferredTabKey: activeTabKey,
                    focus: {
                      type: 'block:value',
                      blockId: idToSourceBlockId({
                        id: block.id,
                        sourceBlockId: block.sourceBlockId,
                      }),
                    },
                  },
                },
                { op: 'blockBatchMutations', args: { mutations: result.undo } },
              ],
            })

            toast.success('Image uploaded successfully', {
              id: toastId,
            })
          }
        } catch (error) {
          console.error(error)
          toast.error('Image could not be uploaded', {
            id: toastId,
          })
        }
      }

      image.src = URL.createObjectURL(file)
    },
    [
      activeTabKey,
      block.id,
      block.instanceChildTarget,
      block.sourceBlockId,
      mutateAsync,
      path,
      replicache,
      repoId,
      teamId,
      undoManager,
    ]
  )

  return (
    <>
      <div className="grid grid-cols-[auto_1fr] grid-rows-[1fr_auto] gap-x-4 mb-8 items-center">
        <SimpleTooltip content="Change avatar">
          <figure
            className={clsx(
              'overflow-hidden relative row-span-full object-cover size-16 border border-dark-border-solid rounded-lg bg-dark-bg',
              editorMode === 'read' || 'cursor-pointer'
            )}
            onClick={() => {
              if (editorMode === 'read') return
              inputRef.current?.click()
            }}
          >
            {block.typeOptions.avatar ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={block.typeOptions.avatar}
                alt=""
                loading="lazy"
                decoding="async"
                className="object-cover w-full h-full pointer-events-none"
              />
            ) : (
              <STARTIcon className="absolute size-12 inset-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
            )}
          </figure>
        </SimpleTooltip>
        <input
          id={`agent-avatar-input-${idToSourceBlockId(block)}`}
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleAvatarChange}
          disabled={editorMode === 'read'}
        />
        <div className="relative overflow-hidden h-full w-[calc(100%+16px)] flex flex-col last:h-auto">
          <ReactTextareaAutosize
            data-document-title={idToSourceBlockId(block)}
            role="document-title"
            className={clsx(
              'w-full h-full -mx-2 overflow-x-hidden overflow-y-auto scroll-my-48 placeholder:text-dark-faint resize-none rounded-md bg-transparent px-2 text-2xl font-medium leading-tight text-dark-bright focus:outline-none cursor-auto break-words whitespace-pre-wrap'
            )}
            placeholder="Untitled"
            value={titleSyncedValue.value}
            onChange={async (e) => {
              // remove line breaks
              const value = e.target.value.replace(/[\r\n]+/g, '')
              await onTitleChange({ value, parentStackItem: null })
            }}
            disabled={
              editorMode === 'read' || editorMode === 'write:value-only'
            }
            ref={titleRef}
            onKeyDown={async (e) => {
              if (
                (isHotkey('ArrowRight', e) &&
                  titleRef.current?.selectionStart ===
                    titleSyncedValue.value.length) ||
                e.key === 'Enter'
              ) {
                // e.preventDefault()
                // e.stopPropagation()
                // TODO: Focus description input
              }
            }}
            onFocus={() => {
              setAiGeneration(null)
            }}
            rows={1}
          />
        </div>
        {block.description && (
          <p className="text-dark-faint text-base pointer-events-none">
            {block.description}
          </p>
        )}
      </div>
      <PromptInput block={block} path={path} />
    </>
  )
}

const PromptInput = ({ block, path }: { block: AgentBlock; path: string }) => {
  const { clientId, replicache } = useRepoReplicache().data ?? {}
  const undoManager = useRepoUndoManager()
  const sourceBlockId = idToSourceBlockId(block)
  const editorMode = useEditorMode()
  const setSelectedBlockAwareness = useEditorStore(
    (s) => s.setSelectedBlockAwareness
  )
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)

  const awarenessList = useBlockAwareness(block.id)
  const isSelected = useEditorStore(
    (s) => s.selectedBlockId === idToSourceBlockId(block)
  )

  const blockAwareness = React.useMemo(
    () => ({
      blockId: block.id,
      isSelected,
      setSelectedBlockAwareness: setSelectedBlockAwareness,
      remoteAwarenessList: awarenessList,
    }),
    [block.id, isSelected, setSelectedBlockAwareness, awarenessList]
  )
  const user = useCollaborationData()
  const [shouldAutoFocus, setShouldAutoFocus] = React.useState(false)
  const systemDefault = React.useMemo(
    () => SYSTEM_AGENTS.find((a) => a.id === block.sourceBlockId),
    [block.sourceBlockId]
  )

  return (
    <>
      <div className="flex justify-between items-center">
        <label
          className="text-sm font-medium h-8 flex items-center flex-grow text-dark-muted"
          onClick={() => {
            setShouldAutoFocus(true)
            setTimeout(() => {
              setShouldAutoFocus(false)
            }, 100)
          }}
        >
          System prompt
        </label>
        {Boolean(
          systemDefault && systemDefault.value.state !== block.value.state
        ) && (
          <Button
            unstyled
            focusStyled
            className="cursor-default text-xs font-medium text-dark-faint hover:text-dark-dim transition-colors shrink-0"
            onClick={async () => {
              if (!replicache || !path || !systemDefault) return
              const isoNow = new Date().toISOString()
              const params: UpdateBlockParams = {
                id: block.id,
                data: {
                  value: {
                    state: systemDefault.value.state,
                    lastRevertedAt: isoNow,
                  },
                  type: block.type,
                },
                instanceChildTarget: block.instanceChildTarget ?? null,
                strategy: 'merge-deep',
                path,
                isoNow,
              }

              const result = await replicache.mutate.updateBlock(params)

              if (result.undo) {
                const stackItem: RepoUndoStackItem = {
                  redo: [
                    getBlockFocusOp({
                      block: {
                        id: block.id,
                        sourceBlockId: block.sourceBlockId,
                      },
                      focusType: 'block:value',
                      preferredTabKey: activeTabKey,
                    }),
                    {
                      op: 'updateBlock',
                      args: params,
                    },
                  ],
                  undo: [
                    getBlockFocusOp({
                      block: {
                        id: block.id,
                        sourceBlockId: block.sourceBlockId,
                      },
                      focusType: 'block:value',
                      preferredTabKey: activeTabKey,
                    }),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                }
                undoManager.current?.push(stackItem)
              }
            }}
          >
            Reset to default
          </Button>
        )}
      </div>
      <PrimitiveTipTapEditor
        autoFocus={shouldAutoFocus}
        key={`agent-prompt-${sourceBlockId}-${block.value.lastRevertedAt}`}
        clientId={clientId}
        readOnly={editorMode === 'read'}
        alwaysShowPlaceholder
        className="rt-editor agent-editor blocked flex flex-col shrink-0"
        value={block.value.state}
        instanceId={`agent-prompt-${sourceBlockId}-${block.value.lastRevertedAt}`}
        getExtensions={getSimpleExtensions}
        blockAwareness={blockAwareness}
        user={user}
        undo={{
          focusOp: {
            op: 'focus',
            args: {
              preferredTabKey: activeTabKey,
              focus: {
                type: 'block:value',
                blockId: idToSourceBlockId(block),
              },
            },
          } satisfies RepoUndoOps[number],
          manager: undoManager.current,
          persist: 'sessionStorage',
        }}
        onUpdate={async (value) => {
          if (!replicache || !path) return
          await replicache.mutate.updateBlock({
            id: block.id,
            data: {
              value: { state: value },
              type: block.type,
            },
            instanceChildTarget: block.instanceChildTarget,
            strategy: 'merge-deep',
            path,
            isoNow: new Date().toISOString(),
          })
        }}
      />
    </>
  )
}
